import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { addDays, startOfDay } from "date-fns";
import { Calendar } from "lucide-react";
import { useState } from "react";
import { type Control, Controller } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderDatePickerDialog } from "web_myplann/Reminders/Components/ReminderDatePickerDialog";
import { SNOOZE_OPTIONS } from "web_myplann/Reminders/Config/snoozeOptions";
import type { ReminderSnoozeFormModel } from "web_myplann/Reminders/Forms/reminderSnoozeSchema";
import { getSnoozeOptionDate } from "web_myplann/Reminders/Utils/snoozeDates";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";

type Props = {
	control: Control<ReminderSnoozeFormModel>;
	minSnoozeDate: Date;
};

export const ReminderFormSnoozeDate = ({ control, minSnoozeDate }: Props) => {
	const { t } = useResource();
	const { palette } = useTheme();

	const [isCustom, setIsCustom] = useState(false);
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

	const minCustomDate = addDays(minSnoozeDate, 1);

	return (
		<Controller
			control={control}
			name="snoozedUntil"
			render={({ field, fieldState }) => {
				const selectRelativeOption = (days: number) => {
					setIsCustom(false);
					field.onChange(getSnoozeOptionDate(minSnoozeDate, days));
				};

				const selectCustomOption = () => {
					setIsCustom(true);
					field.onChange(null);
				};

				return (
					<>
						<Stack direction="row" flexWrap="wrap" gap={1}>
							{SNOOZE_OPTIONS.map((option) => {
								const optionDate = getSnoozeOptionDate(
									minSnoozeDate,
									option.days,
								);
								const isSelected =
									!isCustom && field.value?.getTime() === optionDate.getTime();

								return (
									<Chip
										key={option.days}
										variant="tag"
										color={isSelected ? "info" : "default"}
										label={t(option.labelKey)}
										onClick={() => selectRelativeOption(option.days)}
									/>
								);
							})}
							<Chip
								variant="tag"
								color={isCustom ? "info" : "default"}
								label={t(Resources.Reminders.Snooze.customDate)}
								onClick={selectCustomOption}
							/>
						</Stack>

						{isCustom && (
							<>
								<Box
									component="button"
									type="button"
									onClick={() => setIsDatePickerOpen(true)}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										width: "100%",
										bgcolor: "transparent",
										border: `1px solid ${palette.grey[200]}`,
										borderRadius: 2,
										px: 1.6,
										py: 1.2,
										cursor: "pointer",
										textAlign: "left",
									}}
								>
									<Typography
										variant="body1"
										color={field.value ? undefined : palette.grey[400]}
									>
										{field.value
											? dateFormatter(field.value)
											: t(Resources.Reminders.Snooze.customDate)}
									</Typography>
									<Calendar size={20} color={palette.grey[400]} />
								</Box>
								{fieldState.isDirty && fieldState.error && (
									<Typography variant="hint" color={palette.error.main}>
										{fieldState.error.message}
									</Typography>
								)}
								<ReminderDatePickerDialog
									open={isDatePickerOpen}
									onClose={() => setIsDatePickerOpen(false)}
									onAccept={(value) => field.onChange(startOfDay(value))}
									value={field.value}
									minDate={minCustomDate}
								/>
							</>
						)}
					</>
				);
			}}
		/>
	);
};
